/* spin-wheel/index.js — DOM orchestrator for the Spin the Wheel tool.
 *
 * The only DOM-coupled module of the tool. Wires the blade markup, owns the
 * rAF loop (which runs ONLY while the wheel spins, the spotlight fades in or
 * confetti is live), telemetry, settings, the inline winner reveal, sharing,
 * the results tally and history.
 *
 * The winner is revealed IN the page, never in a modal: a dialog over the
 * wheel hides the very thing the spin was about, and has to be dismissed
 * before the next spin.
 */

import {
    MODES, parseEntries, entriesToText, entriesForMode, buildSlices,
    winnerAtPointer, encodePreset, decodePreset, norm, TAU,
    saveWheel, deleteWheel, findWheel, serializeLibrary, parseLibrary,
    splitPasted, shuffleEntries, sortEntries, dedupeEntries, tallyHistory,
    canEliminate, drawnKey, withoutDrawn, clearDrawnForMode, drawnCountForMode,
} from './wheel-model.js';
import { planSpin, sampleSpin, spinSpeed, pickWeighted, DURATION_MIN_S, DURATION_MAX_S } from './physics.js';
import { drawWheel } from './wheel-render.js';
import { dpr, reducedMotion, onReducedMotionChange } from '../toy-fx/prefs.js';
import { initFunnel } from '../toy-fx/telemetry.js';
import { Confetti } from '../toy-fx/confetti.js';
import { exportPng } from '../toy-fx/canvas-export.js';
import { PALETTES } from '../toy-fx/palette.js';
import * as audio from '../toy-fx/audio.js';

const SLUG = 'spin-wheel';

/* How long the winning slice takes to light up once the wheel stops. */
const SPOTLIGHT_MS = 420;

/* How long an unfinished draw is remembered across reloads. */
const DRAW_TTL_MS = 12 * 60 * 60 * 1000;

const I18N = (typeof window !== 'undefined' && window.SpinWheelI18n) || {};
function t(key, fallback) {
    return (I18N && I18N[key]) || fallback || key;
}

/* Fire a telemetry event, never throwing into the tool. */
function ae(event, payload) {
    try { if (window.__ae) window.__ae(event, Object.assign({ slug: SLUG }, payload || {})); }
    catch (_) {}
}
function funnelStage(name) {
    try { if (window.__stage) window.__stage(name); } catch (_) {}
}

function $(id) { return document.getElementById(id); }

export function mount() {
    const stageEl = $('sw-stage');
    const canvas = $('sw-canvas');
    const fxCanvas = $('sw-fx');
    if (!stageEl || !canvas) return;

    const ctx = canvas.getContext('2d');
    const fxCtx = fxCanvas ? fxCanvas.getContext('2d') : null;
    const confetti = new Confetti();

    const state = {
        mode: 'names',
        title: '',
        entries: parseEntries(t('sample_entries', 'Pizza\nSushi\nTacos\nBurgers\nSalad\nPasta')),
        modeOpts: { diceSides: 6, numMin: 1, numMax: 10, yesNoMaybe: false, teamCount: 2 },
        palette: 'vivid',
        sound: true,
        confetti: true,
        removeWinner: false,
        mystery: false,
        announce: false,
        durationS: 5,
        angle: 0,
        slices: [],
        spinning: false,
        plan: null,
        spinStart: 0,
        lastTickLocal: 0,
        history: [],
        // Labels already taken out of the wheel by elimination mode, keyed by
        // mode. An overlay, not a deletion: the pool the user typed stays whole.
        drawn: new Set(),
        teams: null,
        library: [],
        // Winner presentation.
        winnerIdx: -1,
        spotlight: 0,
        spotStart: 0,
        pointerKick: 0,
    };

    let rafId = 0;
    let lastFrame = 0;
    let eliminateTimer = 0;
    let idleHint = '';
    const geom = { cx: 0, cy: 0, radius: 0 };

    /* ---- settings restore ---- */
    function restoreSettings() {
        try {
            const ts = window.__toolSettings;
            if (!ts) return;
            const sv = ts.load(SLUG, 'mode', 'names');
            if (MODES.indexOf(sv) >= 0) state.mode = sv;
            state.palette = ts.load(SLUG, 'palette', 'vivid');
            state.sound = ts.load(SLUG, 'sound', '1') !== '0';
            state.confetti = ts.load(SLUG, 'confetti', '1') !== '0';
            state.removeWinner = ts.load(SLUG, 'removeWinner', '0') === '1';
            state.mystery = ts.load(SLUG, 'mystery', '0') === '1';
            state.announce = ts.load(SLUG, 'announce', '0') === '1';
            state.title = ts.load(SLUG, 'title', '') || '';
            const d = parseFloat(ts.load(SLUG, 'durationS', '5'));
            if (Number.isFinite(d)) state.durationS = Math.max(DURATION_MIN_S, Math.min(DURATION_MAX_S, d));
            const savedEntries = ts.load(SLUG, 'entries', '');
            if (savedEntries) {
                const parsed = parseEntries(savedEntries);
                if (parsed.length) state.entries = parsed;
            }
            const opts = ts.load(SLUG, 'modeOpts', '');
            if (opts) { try { Object.assign(state.modeOpts, JSON.parse(opts)); } catch (_) {} }
            // A draw in progress survives a reload — losing a half-finished
            // raffle to a stray refresh is the failure people notice. It does
            // NOT survive the night: a teacher who spins the same class list
            // every morning should find a full wheel, not yesterday's
            // leftovers. Twelve hours is "the same sitting".
            const drawn = ts.load(SLUG, 'drawn', '');
            if (drawn) {
                try {
                    const box = JSON.parse(drawn);
                    const keys = Array.isArray(box) ? box : (box && box.k);
                    const at = (box && box.t) || 0;
                    const fresh = Array.isArray(box) || (Date.now() - at) < DRAW_TTL_MS;
                    if (Array.isArray(keys) && fresh) state.drawn = new Set(keys.slice(0, 500));
                } catch (_) {}
            }
        } catch (_) {}
    }
    function persistDrawn() {
        saveSetting('drawn', JSON.stringify({
            // Array.from, NOT slice.call: a Set has no `length`, so the
            // array-like trick silently persists an EMPTY draw — measured,
            // the reload came back with a full wheel and no error anywhere.
            t: Date.now(), k: Array.from(state.drawn),
        }));
    }
    function saveSetting(key, value) {
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, key, value); } catch (_) {}
    }

    /* ---- preset from URL hash ---- */
    function loadHashPreset() {
        const h = (location.hash || '').replace(/^#/, '');
        const m = /(?:^|&)w=([^&]+)/.exec(h);
        if (!m) return false;
        const preset = decodePreset(decodeURIComponent(m[1]));
        if (!preset) return false;
        applyPreset(preset);
        return true;
    }
    function applyPreset(preset) {
        state.drawn = clearDrawnForMode(state.drawn, preset.mode);
        persistDrawn();
        state.mode = preset.mode;
        if (preset.entries.length) state.entries = preset.entries;
        state.palette = preset.palette;
        if (preset.title) state.title = preset.title;
        Object.assign(state.modeOpts, preset.modeOpts);
    }
    function currentPreset() {
        // The POOL, not the half-finished draw: saving or sharing a wheel in
        // the middle of a raffle must hand over everybody, not the leftovers.
        return {
            mode: state.mode, entries: sourceEntries(),
            palette: state.palette, modeOpts: state.modeOpts, title: state.title,
        };
    }

    /* ---- entry lists ----
     * sourceEntries()    — the pool: what the user typed, or what the mode
     *                      generates. Never shrinks during a draw.
     * effectiveEntries() — what is actually on the wheel right now: the pool
     *                      minus everything elimination has already taken out. */
    function sourceEntries() {
        if (isFreeList()) return state.entries;
        return entriesForMode(state.mode, Object.assign({
            words: {
                yes: t('yes_label', 'Yes'),
                no: t('no_label', 'No'),
                maybe: t('maybe_label', 'Maybe'),
            },
        }, state.modeOpts));
    }
    function effectiveEntries() {
        return withoutDrawn(sourceEntries(), state.mode, state.drawn);
    }
    /* True when the wheel is driven by the user's own typed list — the modes
     * that own a text editor and the list tools. Elimination is NOT gated on
     * this any more (see canEliminate): a generated wheel draws down too. */
    function isFreeList() {
        return state.mode === 'names' || state.mode === 'teams';
    }
    function drawnHere() { return drawnCountForMode(state.drawn, state.mode); }
    /* Forget this mode's draw and put everybody back on the wheel. */
    function resetDraw(opts) {
        if (!drawnHere()) return;
        state.drawn = clearDrawnForMode(state.drawn, state.mode);
        persistDrawn();
        if (state.mode === 'teams') {
            state.teams = null;
            const panel = $('sw-teams-result');
            if (panel) panel.hidden = true;
        }
        clearWinner();
        rebuildSlices();
        render();
        if (!(opts && opts.silent) && window.__toast) {
            window.__toast(t('draw_reset_done', 'Everyone is back on the wheel'), 'success');
        }
        ae('feature_use', { f: 'draw_reset', mode: state.mode });
    }

    function rebuildSlices() {
        state.slices = buildSlices(effectiveEntries(), state.palette);
        const n = state.slices.length;
        const out = drawnHere();
        const total = n + out;
        const countEl = $('sw-count');
        if (countEl) {
            // Mid-draw the interesting number is how much of the pool is left,
            // not how many slices happen to be painted.
            countEl.textContent = out
                ? t('entries_remaining', '{n} of {m} left').replace('{n}', n).replace('{m}', total)
                : t('entry_count', '{n} entries').replace('{n}', n);
        }
        const resetBtn = $('sw-reset-draw');
        if (resetBtn) resetBtn.hidden = out === 0;
        canvas.setAttribute('aria-label',
            t('wheel_aria', 'Prize wheel with {n} segments').replace('{n}', n));
        const spinBtn = $('sw-spin');
        if (spinBtn && !state.spinning) spinBtn.disabled = n < 2;
        // A greyed-out button with no explanation is a dead end. Say why — and
        // "add two entries" is the wrong why when the list is full and the
        // draw simply ran out of people.
        const hint = document.querySelector('.sw-spin-hint');
        if (hint) {
            hint.textContent = n >= 2 ? idleHint
                : (out ? t('all_drawn', 'The draw is over. Reset it to spin with everyone again.')
                       : t('need_entries', 'Add at least two entries first.'));
        }
    }

    /* ---- canvas sizing ---- */
    function resize() {
        const rect = stageEl.getBoundingClientRect();
        const size = Math.max(160, Math.floor(Math.min(rect.width, rect.height || rect.width)));
        const r = dpr();
        for (const cv of [canvas, fxCanvas]) {
            if (!cv) continue;
            cv.width = Math.round(size * r);
            cv.height = Math.round(size * r);
            cv.style.width = size + 'px';
            cv.style.height = size + 'px';
        }
        ctx.setTransform(r, 0, 0, r, 0, 0);
        if (fxCtx) fxCtx.setTransform(r, 0, 0, r, 0, 0);
        geom.cx = size / 2;
        geom.cy = size / 2;
        // Leave room for the bezel plus the pointer that sits above it.
        geom.radius = size / 2 - Math.max(18, size * 0.085);
        render();
    }

    /* ---- rendering ---- */
    function render() {
        try {
            drawWheel(ctx, state.slices, state.angle, {
                cx: geom.cx, cy: geom.cy, radius: geom.radius,
                hubText: hubGlyph(),
                mystery: state.mystery && state.spotlight <= 0,
                winner: state.winnerIdx,
                spotlight: state.spotlight,
                pointerKick: state.pointerKick,
            });
        } catch (e) {
            if (window.__handleError) window.__handleError(e, 'spin-wheel.render');
        }
    }

    function hubGlyph() {
        if (state.mystery && state.spotlight <= 0) return '❓';
        if (state.mode === 'dice') return '🎲';
        if (state.mode === 'teams') return '👥';
        if (state.mode === 'yesno') return '🤔';
        return '🎯';
    }

    function renderFx() {
        if (!fxCtx) return;
        fxCtx.clearRect(0, 0, fxCanvas.width, fxCanvas.height);
        confetti.render(fxCtx);
    }

    /* ---- the rAF loop (active only while something animates) ---- */
    function ensureLoop() {
        if (rafId) return;
        lastFrame = performance.now();
        rafId = requestAnimationFrame(frame);
    }
    function frame(now) {
        rafId = 0;
        const dt = Math.min(0.05, (now - lastFrame) / 1000);
        lastFrame = now;
        try {
            let busy = false;
            if (state.spinning) {
                const s = sampleSpin(state.plan, now - state.spinStart);
                state.angle = s.angle;
                emitTicks();
                if (s.done) finishSpin();
                else busy = true;
            }
            // The pointer settles back to rest between ticker kicks.
            if (Math.abs(state.pointerKick) > 0.0015) {
                state.pointerKick *= Math.pow(0.0025, dt);
                busy = true;
            } else if (state.pointerKick !== 0) {
                state.pointerKick = 0;
            }
            // Spotlight fade-in once the wheel has stopped.
            if (state.spotStart && state.spotlight < 1) {
                state.spotlight = Math.max(0, Math.min(1, (now - state.spotStart) / SPOTLIGHT_MS));
                if (state.spotlight < 1) busy = true;
            }
            render();
            if (confetti.active) {
                confetti.step(dt);
                busy = busy || confetti.active > 0;
            }
            renderFx();
            if (busy) { lastFrame = now; rafId = requestAnimationFrame(frame); }
        } catch (e) {
            if (window.__handleError) window.__handleError(e, 'spin-wheel.frame');
        }
    }

    /* ticker-peg clack as the wheel crosses slice boundaries */
    function emitTicks() {
        if (!state.slices.length) return;
        const winIdx = winnerAtPointer(state.slices, state.angle);
        if (winIdx !== state.lastTickLocal) {
            state.lastTickLocal = winIdx;
            const sp = Math.abs(spinSpeed(state.plan, performance.now() - state.spinStart));
            state.pointerKick = Math.min(0.42, 0.12 + sp * 24);
            if (state.sound) audio.tick(Math.min(1, sp * 80));
        }
    }

    /* ---- spinning ---- */
    function startSpin() {
        if (state.spinning) return;
        if (state.slices.length < 2) {
            if (window.__toast) window.__toast(t('need_entries', 'Add at least two entries first'), 'warning');
            return;
        }
        audio.unlock();
        clearWinner();
        const target = pickWeighted(state.slices, Math.random);
        state.plan = planSpin(state.angle, state.slices, target, Math.random, { durationS: state.durationS });
        state.spinning = true;
        state.spinStart = performance.now();
        state.lastTickLocal = winnerAtPointer(state.slices, state.angle);
        setBusy(true);
        funnelStage('spin_start');
        ae('feature_use', { f: 'spin_start', count: state.slices.length, mode: state.mode });
        if (reducedMotion()) {
            // Honour reduced motion: skip the long animation, keep the result.
            state.angle = state.plan.to;
            state.spinning = false;
            setBusy(false);
            finishSpin();
            return;
        }
        ensureLoop();
    }

    function finishSpin() {
        state.spinning = false;
        setBusy(false);
        state.angle = norm(state.angle);
        const idx = winnerAtPointer(state.slices, state.angle);
        const slice = state.slices[idx];
        if (!slice) return;
        const winner = slice.label;
        state.winnerIdx = idx;
        state.spotStart = performance.now();
        state.spotlight = reducedMotion() ? 1 : 0;
        state.history.unshift({ label: winner, at: Date.now() });
        if (state.history.length > 200) state.history.pop();
        renderResults();
        showWinner(winner, slice);
        funnelStage('spin_complete');
        ae('feature_use', { f: 'spin_complete', count: state.slices.length, mode: state.mode });
        try { window.__aeToolComplete && window.__aeToolComplete({ mode: state.mode }); } catch (_) {}
        if (window.__announce) {
            window.__announce(t('winner_announce', 'Winner: {x}').replace('{x}', winner));
        }
        if (reducedMotion()) render();
        else ensureLoop();
    }

    /* ---- the inline winner reveal (no modal) ---- */
    function showWinner(winner, slice) {
        const card = $('sw-result');
        const text = $('sw-winner-text');
        if (text) text.textContent = winner;
        if (card) {
            // Tint the card with the winning slice's own colour so the reveal
            // is visibly tied to the sector that lit up on the wheel.
            card.style.setProperty('--sw-win-color', slice.color);
            card.classList.remove('sw-result-idle');
            card.classList.add('sw-result-win');
            // Restart the pop animation on a re-spin.
            card.classList.remove('sw-result-pop');
            void card.offsetWidth;
            card.classList.add('sw-result-pop');
        }
        const removeBtn = $('sw-remove');
        if (removeBtn) {
            removeBtn.hidden = !canEliminate(state.mode);
            removeBtn.textContent = state.mode === 'teams'
                ? t('assign_team', 'Assign to team')
                : t('remove_winner', 'Remove winner');
        }
        if (state.sound) audio.chime();
        if (state.confetti && !reducedMotion()) {
            confetti.burst(geom.cx, geom.cy, {
                count: 140, power: 640, angle: -Math.PI / 2, spread: Math.PI * 1.6,
            });
            if (state.sound) audio.applause();
        }
        if (state.announce) speakWinner(winner);
        // Elimination mode: the winner leaves the wheel on its own, which is
        // exactly what the toggle's label has always promised. Team mode is
        // an elimination draw by definition — every spin places somebody —
        // so it does not wait for a second click either.
        if (canEliminate(state.mode) && (state.removeWinner || state.mode === 'teams')) {
            clearTimeout(eliminateTimer);
            eliminateTimer = setTimeout(() => {
                if (!state.spinning && state.winnerIdx >= 0) removeWinnerEntry();
            }, 1600);
        }
    }

    function clearWinner() {
        clearTimeout(eliminateTimer);
        state.winnerIdx = -1;
        state.spotlight = 0;
        state.spotStart = 0;
        const card = $('sw-result');
        if (card) {
            card.classList.remove('sw-result-win', 'sw-result-pop');
            card.classList.add('sw-result-idle');
            card.style.removeProperty('--sw-win-color');
        }
    }

    /* Read the winner out loud. Uses the managed front door so a stuck or
     * silent voice is recovered rather than swallowed. */
    function speakWinner(winner) {
        try {
            const say = t('winner_announce', 'Winner: {x}').replace('{x}', winner);
            if (window.__speak) window.__speak(say, { lang: window.__LOCALE || document.documentElement.lang || undefined });
        } catch (_) {}
    }

    /* Take the winner out of the wheel.
     *
     * This used to delete the line from the user's list, which made a raffle a
     * one-way trip: after the last draw the typed names were gone for good
     * (they were persisted in that drained state too) and the only way to run
     * the same draw again was to retype everybody. It marks them drawn now —
     * the pool stays whole, "Reset" brings everyone back, and a generated
     * wheel (numbers, dice) can be drawn down exactly the same way. */
    function removeWinnerEntry() {
        const idx = state.winnerIdx;
        const slice = state.slices[idx];
        if (!slice || !canEliminate(state.mode)) return;
        if (state.mode === 'teams') assignToTeam(slice.label);
        state.drawn.add(drawnKey(state.mode, slice.label));
        persistDrawn();
        clearWinner();
        // The last name left has nothing to be drawn against — spinning a
        // one-slice wheel is not a choice. Place them and finish the draw,
        // instead of leaving the wheel stuck with a disabled spin button.
        if (state.mode === 'teams') {
            const left = effectiveEntries();
            if (left.length === 1) {
                assignToTeam(left[0].label);
                state.drawn.add(drawnKey(state.mode, left[0].label));
                persistDrawn();
            }
        }
        rebuildSlices();
        render();
        ae('feature_use', { f: 'winner_removed', mode: state.mode });
        if (state.mode === 'teams') showTeams();
    }

    /* ---- teams ---- */
    function assignToTeam(labelText) {
        const n = Math.max(2, Math.min(8, state.modeOpts.teamCount || 2));
        if (!state.teams || state.teams.length !== n) {
            state.teams = [];
            for (let i = 0; i < n; i++) state.teams.push([]);
        }
        // Add to the currently smallest team.
        let smallest = 0;
        for (let i = 1; i < state.teams.length; i++) {
            if (state.teams[i].length < state.teams[smallest].length) smallest = i;
        }
        state.teams[smallest].push(labelText);
    }
    function showTeams() {
        const panel = $('sw-teams-result');
        if (!panel || !state.teams) return;
        panel.hidden = false;
        panel.innerHTML = '';
        const head = document.createElement('h3');
        head.className = 'sw-teams-title';
        head.textContent = t('teams_heading', 'Teams');
        panel.appendChild(head);
        const grid = document.createElement('div');
        grid.className = 'sw-teams-grid';
        state.teams.forEach((members, i) => {
            const box = document.createElement('div');
            box.className = 'sw-team';
            const h = document.createElement('strong');
            h.textContent = t('team_label', 'Team {n}').replace('{n}', i + 1);
            box.appendChild(h);
            const ul = document.createElement('div');
            ul.className = 'sw-team-members';
            ul.textContent = members.length ? members.join(', ') : '—';
            box.appendChild(ul);
            grid.appendChild(box);
        });
        panel.appendChild(grid);
        if (!state.entries.length && window.__announce) {
            window.__announce(t('teams_ready', 'Teams are ready'));
        }
    }

    /* ---- results: tally + history ---- */
    function renderResults() {
        const tallyEl = $('sw-tally');
        const list = $('sw-history');
        const empty = $('sw-results-empty');
        const rows = tallyHistory(state.history);
        if (tallyEl) {
            tallyEl.innerHTML = '';
            const max = rows.length ? rows[0].count : 1;
            rows.forEach((r) => {
                const row = document.createElement('div');
                row.className = 'sw-tally-row';
                const name = document.createElement('span');
                name.className = 'sw-tally-name';
                name.textContent = r.label;
                const bar = document.createElement('span');
                bar.className = 'sw-tally-bar';
                const fill = document.createElement('span');
                fill.style.width = Math.round((r.count / max) * 100) + '%';
                bar.appendChild(fill);
                const num = document.createElement('span');
                num.className = 'sw-tally-count';
                num.textContent = String(r.count);
                row.appendChild(name);
                row.appendChild(bar);
                row.appendChild(num);
                tallyEl.appendChild(row);
            });
        }
        if (list) {
            list.innerHTML = '';
            state.history.slice(0, 30).forEach((h) => {
                const li = document.createElement('li');
                li.textContent = h.label;
                list.appendChild(li);
            });
        }
        if (empty) empty.hidden = state.history.length > 0;
        const total = $('sw-spin-total');
        if (total) total.textContent = String(state.history.length);
        const badge = $('sw-tab-badge');
        if (badge) {
            badge.textContent = String(state.history.length);
            badge.hidden = state.history.length === 0;
        }
        const bar = $('sw-results-actions');
        if (bar) bar.hidden = state.history.length === 0;
    }
    function exportHistory() {
        if (!state.history.length) return;
        const lines = [];
        if (state.title) { lines.push(state.title); lines.push(''); }
        lines.push(t('history', 'Spin history'));
        state.history.forEach((h, i) => {
            lines.push(`${i + 1}. ${h.label}  —  ${new Date(h.at).toLocaleString()}`);
        });
        lines.push('');
        lines.push(t('tally', 'Wins per entry'));
        tallyHistory(state.history).forEach((r) => lines.push(`${r.label}: ${r.count}`));
        const blob = new Blob([lines.join('\r\n')], { type: 'text/plain;charset=utf-8' });
        try { window.__downloadFile(blob, 'spin-wheel-results.txt'); } catch (_) {}
        ae('feature_use', { f: 'history_export' });
    }
    function clearHistory() {
        state.history = [];
        state.teams = null;
        const panel = $('sw-teams-result');
        if (panel) panel.hidden = true;
        renderResults();
        ae('feature_use', { f: 'history_clear' });
    }

    /* ---- entry editing ---- */
    function syncEntriesTextarea() {
        const ta = $('sw-entries');
        if (ta) ta.value = entriesToText(state.entries);
    }
    function persistEntries() {
        saveSetting('entries', entriesToText(state.entries));
    }
    function onEntriesInput() {
        const ta = $('sw-entries');
        if (!ta) return;
        state.entries = parseEntries(ta.value);
        persistEntries();
        clearWinner();
        rebuildSlices();
        render();
    }
    /* Apply a list transform (shuffle / sort / dedupe / clear) and re-render. */
    function transformEntries(fn, evt) {
        if (!isFreeList()) return;
        const before = state.entries.length;
        state.entries = fn(state.entries);
        syncEntriesTextarea();
        persistEntries();
        clearWinner();
        rebuildSlices();
        render();
        if (evt === 'dedupe' && window.__toast) {
            const gone = before - state.entries.length;
            window.__toast(gone
                ? t('dedupe_done', 'Removed {n} duplicates').replace('{n}', gone)
                : t('dedupe_none', 'No duplicates found'), gone ? 'success' : 'info');
        }
        ae('feature_use', { f: 'list_' + evt });
    }

    /* ---- mode UI ---- */
    function applyModeUI() {
        ['dice', 'numbers', 'yesno', 'teams'].forEach((m) => {
            const el = $('sw-opts-' + m);
            if (el) el.hidden = (state.mode !== m);
        });
        const editor = $('sw-entry-editor');
        if (editor) editor.hidden = !isFreeList();
        const teamsPanel = $('sw-teams-result');
        if (teamsPanel && state.mode !== 'teams') teamsPanel.hidden = true;
        document.querySelectorAll('.sw-mode-chip').forEach((b) => {
            const on = b.dataset.mode === state.mode;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        applyElimUI();
    }

    /* The elimination switch is ALWAYS on screen.
     *
     * It used to disappear in every generated mode. A control that vanishes
     * cannot say why it vanished — and on a site that sells Premium the reader
     * fills the gap themselves: bug report #10000715 asked whether the
     * elimination mode "has to be premium". It is free, it was simply hidden.
     * So: available everywhere it means something, and where it does not, it
     * stays visible, switched off, with the reason next to it. */
    function applyElimUI() {
        const row = $('sw-row-remove');
        const box = $('sw-remove-winner');
        const why = $('sw-remove-desc');
        const on = canEliminate(state.mode);
        if (row) {
            row.hidden = false;
            row.classList.toggle('is-off', !on);
        }
        if (box) {
            box.disabled = !on;
            box.checked = on && !!state.removeWinner;
        }
        if (why) {
            why.textContent = on
                ? t('opt_remove_desc', 'The winner leaves the wheel after each spin. Your list stays whole — Reset puts everyone back.')
                : t('opt_remove_na', 'Not for the Yes/No wheel: drop one answer and every later spin is decided in advance.');
        }
    }

    /* ---- sharing ---- */
    function shareLink() {
        const preset = encodePreset(currentPreset());
        const url = location.origin + location.pathname + '#w=' + encodeURIComponent(preset);
        history.replaceState(null, '', '#w=' + encodeURIComponent(preset));
        ae('feature_use', { f: 'share_copied' });
        // A native share sheet is the right surface on a phone; the clipboard
        // is the right one on a desktop.
        let coarse = false;
        try { coarse = matchMedia('(pointer: coarse)').matches; } catch (_) {}
        if (navigator.share && coarse) {
            navigator.share({ title: state.title || document.title, url: url }).catch(() => {});
            return;
        }
        copyText(url, t('link_copied', 'Share link copied'));
    }
    function copyText(text, toastMsg) {
        const done = () => {
            if (toastMsg && window.__toast) window.__toast(toastMsg, 'success');
        };
        const fail = () => prompt(t('share_prompt', 'Copy this link:'), text);
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(done, fail);
        } else {
            fail();
        }
    }

    /* Save the wheel as a PNG — the shareable artefact for a class list or a
     * giveaway announcement. Rendered fresh at 2× so it stays crisp. */
    function downloadPng() {
        const size = 1000;
        const top = state.title ? 96 : 44;
        exportPng((c, w, h) => {
            c.fillStyle = '#0b1220';
            c.fillRect(0, 0, w, h);
            const R = (Math.min(w, h) - top - 44) / 2;
            drawWheel(c, state.slices, state.angle, {
                cx: w / 2, cy: top + R, radius: R * 0.90,
                hubText: hubGlyph(), flat: true,
                winner: state.winnerIdx, spotlight: state.spotlight,
                title: state.title || '',
            });
        }, { w: size, h: size, scale: 2, bg: '#0b1220' })
            .then((blob) => {
                const name = (state.title || 'spin-wheel')
                    .replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'spin-wheel';
                window.__downloadFile(blob, name + '.png');
                if (window.__toast) window.__toast(t('png_saved', 'Image saved'), 'success');
                ae('feature_use', { f: 'png_export' });
            })
            .catch((e) => { if (window.__handleError) window.__handleError(e, 'spin-wheel.png'); });
    }

    /* ---- saved-wheels library ---- */
    function loadLibrary() {
        try {
            const ts = window.__toolSettings;
            state.library = ts ? parseLibrary(ts.load(SLUG, 'library', '[]')) : [];
        } catch (_) { state.library = []; }
    }
    function persistLibrary() {
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'library', serializeLibrary(state.library)); }
        catch (_) {}
    }
    function renderLibrary() {
        const sel = $('sw-saved');
        if (!sel) return;
        sel.innerHTML = '';
        const ph = document.createElement('option');
        ph.value = '';
        ph.textContent = t('saved_pick', 'Load a saved wheel…');
        sel.appendChild(ph);
        state.library.forEach((w) => {
            const o = document.createElement('option');
            o.value = w.id;
            o.textContent = w.name;
            sel.appendChild(o);
        });
        const delBtn = $('sw-delete-wheel');
        if (delBtn) delBtn.disabled = state.library.length === 0;
        sel.disabled = state.library.length === 0;
    }
    function saveCurrentWheel() {
        const suggested = state.title || t('default_wheel_name', 'My wheel');
        const name = prompt(t('save_wheel_prompt', 'Name this wheel:'), suggested);
        if (name == null) return;
        state.library = saveWheel(state.library, name, currentPreset());
        persistLibrary();
        renderLibrary();
        if (window.__toast) window.__toast(t('wheel_saved', 'Wheel saved'), 'success');
        ae('feature_use', { f: 'wheel_saved' });
    }
    function loadSavedWheel(id) {
        const w = findWheel(state.library, id);
        if (!w) return;
        const preset = decodePreset(w.preset);
        if (!preset) return;
        if (!preset.title) preset.title = w.name;
        applyPreset(preset);
        state.teams = null;
        clearWinner();
        syncEntriesTextarea();
        applyModeUI();
        rebuildSlices();
        render();
        syncControlsFromState();
        ae('feature_use', { f: 'wheel_loaded' });
    }
    function deleteSavedWheel() {
        const sel = $('sw-saved');
        if (!sel || !sel.value) {
            if (window.__toast) window.__toast(t('pick_wheel_first', 'Pick a saved wheel first'), 'warning');
            return;
        }
        state.library = deleteWheel(state.library, sel.value);
        persistLibrary();
        renderLibrary();
        ae('feature_use', { f: 'wheel_deleted' });
    }
    /* Push restored values back into the controls. */
    function syncControlsFromState() {
        const titleEl = $('sw-title');
        if (titleEl) titleEl.value = state.title;
        document.querySelectorAll('.sw-swatch').forEach((b) => {
            const on = b.dataset.palette === state.palette;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const dur = $('sw-duration');
        if (dur) dur.value = String(state.durationS);
        showDuration();
        renderTitleCaption();
    }
    /* Paint each palette swatch as a miniature of the wheel it produces.
     * Reading the colours from PALETTES rather than repeating them in CSS
     * means a palette edit can never leave the picker showing the old one. */
    function paintSwatches() {
        document.querySelectorAll('.sw-swatch').forEach((b) => {
            const dot = b.querySelector('i');
            const colors = PALETTES[b.dataset.palette];
            if (!dot || !colors) return;
            const step = 100 / colors.length;
            const stops = colors.map((c, i) => `${c} ${i * step}% ${(i + 1) * step}%`).join(',');
            dot.style.background = `conic-gradient(${stops})`;
        });
    }
    function showDuration() {
        const out = $('sw-duration-val');
        if (out) out.textContent = t('seconds_short', '{n} s').replace('{n}', state.durationS);
    }
    function renderTitleCaption() {
        const cap = $('sw-title-caption');
        if (!cap) return;
        cap.textContent = state.title;
        cap.hidden = !state.title;
    }

    /* ---- fullscreen ---- */
    function toggleFullscreen() {
        const target = $('sw-fs-root') || stageEl;
        if (document.fullscreenElement) {
            if (document.exitFullscreen) document.exitFullscreen();
            return;
        }
        if (target.requestFullscreen) {
            target.requestFullscreen().catch(() => {});
            ae('feature_use', { f: 'fullscreen' });
        } else if (window.__toast) {
            window.__toast(t('fullscreen_unavailable', 'Fullscreen is not available here'), 'warning');
        }
    }

    function setBusy(busy) {
        const btn = $('sw-spin');
        if (btn) btn.disabled = busy || state.slices.length < 2;
        stageEl.classList.toggle('sw-spinning', busy);
        const wrap = $('sw-fs-root');
        if (wrap) wrap.classList.toggle('is-spinning', busy);
    }

    /* ---- pointer flick to spin ---- */
    function setupFlick() {
        let down = false, lastA = 0, lastT = 0, vel = 0, moved = 0;
        const centerAngle = (e) => {
            const rect = canvas.getBoundingClientRect();
            return Math.atan2(e.clientY - (rect.top + rect.height / 2),
                              e.clientX - (rect.left + rect.width / 2));
        };
        canvas.addEventListener('pointerdown', (e) => {
            if (state.spinning) return;
            down = true;
            moved = 0;
            lastA = centerAngle(e);
            lastT = performance.now();
            vel = 0;
            try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
        });
        canvas.addEventListener('pointermove', (e) => {
            if (!down) return;
            const a = centerAngle(e);
            const now = performance.now();
            let da = a - lastA;
            if (da > Math.PI) da -= TAU;
            if (da < -Math.PI) da += TAU;
            const dt = Math.max(1, now - lastT);
            vel = da / dt;
            moved += Math.abs(da);
            state.angle += da;
            lastA = a;
            lastT = now;
            render();
        });
        const release = () => {
            if (!down) return;
            down = false;
            if (Math.abs(vel) > 0.003) startSpin();
        };
        canvas.addEventListener('pointerup', release);
        canvas.addEventListener('pointercancel', release);
        canvas.addEventListener('click', () => {
            // A plain tap (no drag) also spins.
            if (!state.spinning && moved < 0.05) startSpin();
        });
    }

    /* ---- tabs ---- */
    function setupTabs() {
        const tabs = Array.prototype.slice.call(document.querySelectorAll('.sw-tab-btn'));
        const panels = Array.prototype.slice.call(document.querySelectorAll('.sw-tabpanel'));
        if (!tabs.length) return;
        function select(name) {
            tabs.forEach((b) => {
                const on = b.dataset.tab === name;
                b.classList.toggle('is-active', on);
                b.setAttribute('aria-selected', on ? 'true' : 'false');
                b.tabIndex = on ? 0 : -1;
            });
            panels.forEach((p) => { p.hidden = p.dataset.tab !== name; });
        }
        tabs.forEach((b) => b.addEventListener('click', () => select(b.dataset.tab)));
        // Roving focus, as the tablist role promises.
        tabs.forEach((b, i) => b.addEventListener('keydown', (e) => {
            let j = -1;
            if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
            if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
            if (j < 0) return;
            e.preventDefault();
            tabs[j].focus();
            select(tabs[j].dataset.tab);
        }));
        select('entries');
    }

    /* ---- wire controls ---- */
    function wire() {
        bindClick('sw-spin', startSpin);

        const ta = $('sw-entries');
        if (ta) {
            ta.value = entriesToText(state.entries);
            ta.addEventListener('input', onEntriesInput);
            // Pasting a CSV row or a spreadsheet column should become one
            // entry per line instead of one giant entry.
            ta.addEventListener('paste', (e) => {
                const raw = e.clipboardData && e.clipboardData.getData('text/plain');
                if (!raw) return;
                const split = splitPasted(raw);
                if (split === raw) return;
                e.preventDefault();
                const start = ta.selectionStart, end = ta.selectionEnd;
                ta.value = ta.value.slice(0, start) + split + ta.value.slice(end);
                ta.selectionStart = ta.selectionEnd = start + split.length;
                onEntriesInput();
                ae('feature_use', { f: 'smart_paste' });
            });
        }

        const titleEl = $('sw-title');
        if (titleEl) {
            titleEl.value = state.title;
            titleEl.addEventListener('input', () => {
                state.title = titleEl.value.slice(0, 80);
                saveSetting('title', state.title);
                renderTitleCaption();
            });
        }

        document.querySelectorAll('.sw-mode-chip').forEach((b) => {
            b.addEventListener('click', () => {
                state.mode = b.dataset.mode;
                state.teams = null;
                saveSetting('mode', state.mode);
                clearWinner();
                applyModeUI();
                rebuildSlices();
                render();
                ae('feature_use', { f: 'mode_change', mode: state.mode });
            });
        });

        document.querySelectorAll('.sw-swatch').forEach((b) => {
            b.addEventListener('click', () => {
                state.palette = b.dataset.palette;
                saveSetting('palette', state.palette);
                rebuildSlices();
                render();
                syncControlsFromState();
                ae('feature_use', { f: 'palette', p: state.palette });
            });
        });

        bindClick('sw-shuffle', () => transformEntries((e) => shuffleEntries(e), 'shuffle'));
        bindClick('sw-sort', () => transformEntries((e) => sortEntries(e, window.__LOCALE), 'sort'));
        bindClick('sw-dedupe', () => transformEntries(dedupeEntries, 'dedupe'));
        bindClick('sw-clear-entries', () => {
            if (!state.entries.length) return;
            resetDraw({ silent: true });
            transformEntries(() => [], 'clear');
        });

        bindOpt('sw-dice-sides', 'diceSides', true);
        bindOpt('sw-num-min', 'numMin', true);
        bindOpt('sw-num-max', 'numMax', true);
        bindOpt('sw-team-count', 'teamCount', true);
        const ynMaybe = $('sw-yn-maybe');
        if (ynMaybe) {
            ynMaybe.checked = !!state.modeOpts.yesNoMaybe;
            ynMaybe.addEventListener('change', () => {
                state.modeOpts.yesNoMaybe = ynMaybe.checked;
                persistModeOpts();
                resetDraw({ silent: true });
                clearWinner(); rebuildSlices(); render();
            });
        }

        const dur = $('sw-duration');
        if (dur) {
            dur.min = String(DURATION_MIN_S);
            dur.max = String(DURATION_MAX_S);
            dur.value = String(state.durationS);
            dur.addEventListener('input', () => {
                state.durationS = parseFloat(dur.value) || 5;
                showDuration();
            });
            dur.addEventListener('change', () => {
                saveSetting('durationS', String(state.durationS));
                ae('feature_use', { f: 'duration', v: state.durationS });
            });
        }

        bindToggle('sw-sound', 'sound', (v) => { audio.setMuted(!v); });
        bindToggle('sw-confetti', 'confetti');
        bindToggle('sw-remove-winner', 'removeWinner', (v) => {
            // Nothing measured this before, so "is elimination even used?" had
            // to be answered from winner_removed alone — which cannot see the
            // people who looked for the switch and did not find it.
            ae('feature_use', { f: 'elimination_toggle', on: v ? 1 : 0, mode: state.mode });
        });
        bindToggle('sw-mystery', 'mystery', () => { render(); });
        bindToggle('sw-announce', 'announce');
        audio.setMuted(!state.sound);

        bindClick('sw-reset-draw', () => resetDraw());
        bindClick('sw-share', shareLink);
        bindClick('sw-fullscreen', toggleFullscreen);
        bindClick('sw-png', downloadPng);
        bindClick('sw-history-export', exportHistory);
        bindClick('sw-history-clear', clearHistory);

        bindClick('sw-save-wheel', saveCurrentWheel);
        bindClick('sw-delete-wheel', deleteSavedWheel);
        const savedSel = $('sw-saved');
        if (savedSel) savedSel.addEventListener('change', () => {
            if (savedSel.value) loadSavedWheel(savedSel.value);
        });

        // Inline result actions.
        bindClick('sw-spin-again', startSpin);
        bindClick('sw-remove', removeWinnerEntry);
        bindClick('sw-copy-result', () => {
            const txt = $('sw-winner-text');
            if (txt) copyText(txt.textContent || '', t('copied', 'Copied'));
        });
        bindClick('sw-speak-result', () => {
            const txt = $('sw-winner-text');
            if (txt && txt.textContent) speakWinner(txt.textContent);
        });

        // Space / Enter spins — but only when the user is not on a control of
        // their own. Hijacking Space while a button has focus means pressing
        // "Share" silently spins the wheel instead of sharing.
        document.addEventListener('keydown', (e) => {
            if (e.key !== ' ' && e.key !== 'Enter') return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const el = e.target;
            if (el && (el.isContentEditable
                || /^(INPUT|TEXTAREA|SELECT|BUTTON|A|SUMMARY|OPTION)$/.test(el.tagName || ''))) return;
            if (!isOnScreen(stageEl)) return;
            e.preventDefault();
            startSpin();
        });

        if (window.ResizeObserver) {
            new ResizeObserver(debounce(resize, 60)).observe(stageEl);
        } else {
            window.addEventListener('resize', debounce(resize, 120));
        }
        document.addEventListener('fullscreenchange', () => {
            const wrap = $('sw-fs-root');
            if (wrap) wrap.classList.toggle('is-fullscreen', !!document.fullscreenElement);
            setTimeout(resize, 60);
        });
        onReducedMotionChange(() => { state.spotlight = state.winnerIdx >= 0 ? 1 : 0; render(); });
        window.addEventListener('beforeunload', () => { if (rafId) cancelAnimationFrame(rafId); });
        setupFlick();
        setupTabs();
    }

    /* Only claim the spacebar while the wheel is actually in view. */
    function isOnScreen(el) {
        const r = el.getBoundingClientRect();
        return r.bottom > 0 && r.top < (window.innerHeight || 0);
    }

    function bindOpt(id, key, isInt) {
        const el = $(id);
        if (!el) return;
        el.value = state.modeOpts[key];
        el.addEventListener('change', () => {
            state.modeOpts[key] = isInt ? (parseInt(el.value, 10) || 0) : el.value;
            persistModeOpts();
            // Re-configuring the pool (a new number range, a different die) is
            // setting up a new draw, so the old one's leftovers go with it.
            resetDraw({ silent: true });
            clearWinner();
            rebuildSlices();
            render();
        });
    }
    function persistModeOpts() {
        saveSetting('modeOpts', JSON.stringify(state.modeOpts));
    }
    function bindToggle(id, key, extra) {
        const el = $(id);
        if (!el) return;
        el.checked = !!state[key];
        el.addEventListener('change', () => {
            state[key] = el.checked;
            saveSetting(key, el.checked ? '1' : '0');
            if (extra) extra(el.checked);
        });
    }
    function bindClick(id, fn) {
        const el = $(id);
        if (el) el.addEventListener('click', fn);
    }

    /* ---- boot ---- */
    const hintEl = document.querySelector('.sw-spin-hint');
    idleHint = hintEl ? hintEl.textContent : '';
    restoreSettings();
    loadLibrary();
    const fromHash = loadHashPreset();
    wire();
    applyModeUI();
    paintSwatches();
    syncControlsFromState();
    rebuildSlices();
    resize();
    renderResults();
    renderLibrary();
    if (fromHash && window.__toast) {
        window.__toast(t('preset_loaded', 'Shared wheel loaded'), 'info');
        ae('feature_use', { f: 'preset_loaded' });
    }
    try { initFunnel(SLUG); } catch (_) {}

    /* expose a tiny debug hook for the Playwright smoke test */
    window.__swDebug = {
        spin: startSpin,
        state: state,
        winnerNow: () => {
            const i = winnerAtPointer(state.slices, state.angle);
            return state.slices[i] ? state.slices[i].label : null;
        },
        setEntries: (text) => {
            state.entries = parseEntries(text);
            syncEntriesTextarea();
            clearWinner();
            rebuildSlices(); render();
        },
        setDuration: (s) => { state.durationS = s; },
        removeWinner: removeWinnerEntry,
        setMode: (m) => {
            state.mode = m;
            state.teams = null;
            saveSetting('mode', m);
            clearWinner(); applyModeUI(); rebuildSlices(); render();
        },
        resetDraw: () => resetDraw({ silent: true }),
        drawnCount: () => drawnHere(),
    };
}

function debounce(fn, ms) {
    let timer = 0;
    return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, ms);
    };
}

/* Auto-mount once the DOM is ready. */
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mount);
    } else {
        mount();
    }
}
